/**
 * Экран разобранного чека — по макету «Распознавание чека» (Figma, 21:368).
 *
 * Один и тот же для снимка («Чек») и электронного документа: человек видит
 * одинаковый чек, откуда бы он ни пришёл. Порядок сверху вниз:
 *
 * · сумма крупно и плашка «Найдено N товаров», рядом — посмотреть сам чек;
 * · карточка «Счёт» и «Дата и время»;
 * · пояснение «Умное разделение чека» — только когда категорий больше одной,
 *   и его можно убрать навсегда;
 * · «Распознанные позиции»: у каждой справа сумма и категория-плашка, тап по
 *   плашке меняет категорию;
 * · внизу, поверх списка, — итог и «Записать».
 *
 * Значки — Font Awesome, как во всём приложении; значок счёта и значок
 * категории — те, что человек выбрал для них сам.
 */
import { useState, type ReactNode } from 'react';
import { formatMoney, kop, type Account } from '@skoka/core';
import type { Category } from './categories.js';
import { CategoryField } from './operation-fields.js';
import { CardAccount } from './card-fields.js';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { CategoryGlyph, colorOf } from './category-icon.js';
import { plural } from './text.js';
import { CloseX } from './close-icon.js';
import { NewTag } from './new-categories.js';

const Chevron = () => <Icon name="chevron-right" size={14} className="rcchev" />;

/** Товар, как его показывает экран: количество, цена и сумма в копейках. */
export interface ReceiptLine {
  name: string;
  qty: number;
  price: number;
  sum: number;
}

/** Сумма крупно, под ней «Найдено N товаров» и «Просмотреть» сам чек. */
export function ReceiptHead({ total, count, preview }: {
  total: number; count: number;
  /** Снимок или страница документа; нет — кнопки «Просмотреть» нет. */
  preview?: string | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="rcamount num">{formatMoney(kop(total))}</div>
      <div className="rcstrip">
        <span className="rcinfo">Найдено {plural(count, 'товар', 'товара', 'товаров')}</span>
        {preview && (
          <button className={`rcview${open ? ' on' : ''}`} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            Просмотреть
            <Icon name="chevron-down" size={12} />
          </button>
        )}
      </div>
      {open && preview && <img className="rcshot" src={preview} alt="Снимок чека" />}
    </>
  );
}

/** Карточка «Счёт» и «Дата и время». */
export function ReceiptSelectors({ accounts, accId, onAccount, when, extra }: {
  accounts: Account[];
  accId: string;
  onAccount: (id: string) => void;
  /** «Вчера, 19:14» и, если дату можно поменять, поле поверх строки. */
  when: { label: string; input?: ReactNode };
  /** Дополнительная строка карточки — например, категория всего чека. */
  extra?: ReactNode;
}) {
  return (
    <div className="rccard rcsel">
      <CardAccount label="Счёт" accounts={accounts} value={accId} onChange={onAccount} />
      <div className="rcselrow">
        <span className="rclabel">Дата и время</span>
        <span className="rcvalue">
          <span className="rcvaltext">{when.label}</span>
          {when.input && <Chevron />}
          {when.input}
        </span>
      </div>
      {extra}
    </div>
  );
}

const SPLIT_KEY = 'skoka.splitInfo';
const splitHidden = () => { try { return localStorage.getItem(SPLIT_KEY) === 'off'; } catch { return false; } };

/**
 * «Умное разделение чека»: почему одна покупка станет несколькими
 * операциями. Показывается, только когда категорий в чеке больше одной.
 * «Понятно, больше не показывать» убирает его насовсем, крестик — до
 * следующего чека.
 */
export function SplitBanner({ show }: { show: boolean }) {
  const [closed, setClosed] = useState(splitHidden);
  if (!show || closed) return null;
  return (
    <div className="rcsplit">
      <div className="rcsplithead">
        <span className="rcsplitico"><Icon name="split" size={13} /></span>
        <div className="rcsplittext">
          <b>Умное разделение чека</b>
          <p>
            В чеке товары из разных категорий. SKOKA разобьёт его на несколько
            операций — по одной на категорию, чтобы «Аналитика» была точной.
          </p>
        </div>
      </div>
      <button className="rcsplitok" onClick={() => {
        try { localStorage.setItem(SPLIT_KEY, 'off'); } catch { /* приватный режим */ }
        setClosed(true);
      }}>Понятно, больше не показывать</button>
      <button className="rcsplitx" aria-label="Скрыть" onClick={() => setClosed(true)}><CloseX /></button>
    </div>
  );
}

/** «1 шт × 113,89 ₽»; вес — запятой, как на ценнике. */
const eachOf = (l: ReceiptLine) => (Number.isInteger(l.qty)
  ? `${l.qty} шт × ${formatMoney(kop(l.price))}`
  : `${String(l.qty).replace('.', ',')} × ${formatMoney(kop(l.price))}`);

/**
 * «Распознанные позиции».
 *
 * Название — в одну строку: целиком оно видно в листе позиции. Категория —
 * плашкой справа; тап по ней открывает справочник. Категорию, которую модель
 * не узнала, плашка обводит пунктиром: её стоит проверить.
 */
export function ReceiptItems({ lines, categories, onCategories, categoryOf, unsure, isNew, onPick, onOpen }: {
  lines: ReceiptLine[];
  categories: Category[];
  onCategories: (c: Category[]) => void;
  /** Категория позиции; `null` — чек записывается одной тратой, плашек нет. */
  categoryOf: (n: number) => string | null;
  unsure: (n: number) => boolean;
  /** Категория позиции новая — появится при «Записать»: пометка «новая». */
  isNew?: (n: number) => boolean;
  onPick: (n: number, categoryId: string) => void;
  /** Тап по названию: правка позиции. Нет — название не нажимается. */
  onOpen?: (n: number) => void;
}) {
  return (
    <div className="rccard rcitems">
      <div className="rcitemshead">
        <b>Распознанные позиции
          {lines.some((_, n) => categoryOf(n) !== null) && (
            <> <Tip>Нажми на категорию справа, чтобы поменять её у этого товара.</Tip></>
          )}</b>
        <span>{plural(lines.length, 'товар', 'товара', 'товаров')}</span>
      </div>
      <div className="rcrows">
        {lines.map((l, n) => {
          const id = categoryOf(n);
          const each = <span>{eachOf(l)}{isNew?.(n) && <> · <NewTag /></>}</span>;
          return (
            <div className="rcrow" key={`${l.name}-${n}`}>
              {onOpen
                ? <button className="rcname" onClick={() => onOpen(n)} aria-label={`Позиция «${l.name}»`}><b>{l.name}</b>{each}</button>
                : <div className="rcname"><b>{l.name}</b>{each}</div>}
              <div className="rcright">
                <b className="num">{formatMoney(kop(l.sum))}</b>
                {id !== null && (
                  <CategoryField categories={categories} kind="expense" value={id} onUpdated={onCategories}
                    onChange={(next) => onPick(n, next)}
                    trigger={(open, current) => (
                      <button className={`rcpill c-${colorOf(current)}${unsure(n) ? ' guess' : ''}`} onClick={open}
                        aria-label={`Категория для «${l.name}»: ${current?.name ?? 'не выбрана'}`}>
                        {current && <CategoryGlyph icon={current.icon} color={colorOf(current)} size="xs" />}
                        <span>{current?.name ?? 'Категория'}</span>
                        <Chevron />
                      </button>
                    )} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Низ листа поверх списка: итог, «Записать», «Не записывать». */
export function ReceiptBar({ total, label, disabled, onSave, onCancel }: {
  total: number;
  label: string;
  disabled: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="rcbar">
      <div className="rctotal"><span>Итого:</span><b className="num">{formatMoney(kop(total))}</b></div>
      <button className="rccta" disabled={disabled} onClick={onSave}>{label}</button>
      <button className="rccancel" onClick={onCancel}>Не записывать</button>
    </div>
  );
}
